import {
  CollectionReference,
  DocumentReference,
  DocumentSnapshot,
  Query,
  QuerySnapshot,
} from './refs'
import { Store, type SnapshotTarget, type StoreOptions, type WriteItem, type WriteReceipt } from './store'
import type { Data } from './types'
import { newId as newIdLocal } from './ulid'

export { collection, doc, query, where, orderBy, limit } from './refs'
export {
  CollectionReference,
  DocumentReference,
  DocumentSnapshot,
  QueryDocumentSnapshot,
  QuerySnapshot,
  Query,
} from './refs'
export type { Constraint, WhereOp } from './refs'
export { Store, createStore, LOCAL_ONLY, LAZY_COLLECTIONS, MAX_BATCH_OPS } from './store'
export type { StoreOptions, WriteItem, WriteReceipt, SnapshotTarget } from './store'
export { newId } from './ulid'
export { applyOp, DEL_KEY } from './util'
export { memoryDriver } from './drivers/memory'
export { sqliteDriver } from './drivers/sqlite'
export { idbDriver } from './drivers/idb'
export { webTabs, electLeader } from './tabs'
export type { TabCoordinator, TabMessage } from './tabs'
export type {
  ApplyRemoteOpts,
  CommitTx,
  Data,
  DocRow,
  Driver,
  FailedOp,
  Identity,
  Op,
  OpMode,
  PathMapper,
  RemoteChange,
} from './types'
export type { MemoryDriver } from './drivers/memory'
export type { SqliteDriverOptions, SqlDatabase, SqlExecutor } from './drivers/sqlite'
export type { IdbDriverOptions } from './drivers/idb'

// ---- the process-wide store the Firestore-named functions talk to ----

interface Sub {
  target: SnapshotTarget
  next: (snap: never) => void
  error?: (err: unknown) => void
  detach: (() => void) | null
}

let current: Store | null = null
let opening: Promise<Store> | null = null
let waiters: { resolve: (s: Store) => void; reject: (e: unknown) => void }[] = []
const subs = new Set<Sub>()
const writeErrorSubs = new Set<(err: unknown) => void>()
let detachWriteErrors: (() => void) | null = null

function ready(): Promise<Store> {
  if (current) return Promise.resolve(current)
  if (opening) return opening
  return new Promise((resolve, reject) => waiters.push({ resolve, reject }))
}

export function openStore(opts: StoreOptions): Promise<Store> {
  if (current || opening) closeStore()
  const p = new Store(opts).open()
  opening = p
  p.then(
    (store) => {
      if (opening !== p) return store.close()
      opening = null
      current = store
      for (const s of subs) s.detach = store.onSnapshot(s.target, s.next, s.error)
      detachWriteErrors = store.onWriteError((err) => { for (const cb of [...writeErrorSubs]) { try { cb(err) } catch { /* listener */ } } })
      for (const w of waiters.splice(0)) w.resolve(store)
    },
    (err) => {
      if (opening !== p) return
      opening = null
      for (const w of waiters.splice(0)) w.reject(err)
    },
  )
  return p
}

export function getStore(): Store {
  if (!current) throw new Error('localstore: openStore() has not resolved yet')
  return current
}

export function whenStoreReady(): Promise<Store> {
  return ready()
}

/** A local save failed (the store stays usable; the app decides whether to reopen it). Survives reopens. */
export function onStoreWriteError(cb: (err: unknown) => void): () => void {
  writeErrorSubs.add(cb)
  return () => writeErrorSubs.delete(cb)
}

function detachAll(): void {
  for (const s of subs) {
    s.detach?.()
    s.detach = null
  }
  detachWriteErrors?.()
  detachWriteErrors = null
}

// Listeners registered through onSnapshot stay registered and re-attach to the next store.
export function closeStore(): void {
  detachAll()
  current?.close()
  current = null
  opening = null
}

/** Let queued writes finish, then release the database — before an in-app restart, or to reopen a stuck one. */
export async function shutdownStore(): Promise<void> {
  const s = current
  if (s) await s.shutdown()
  if (current !== s) return
  detachAll()
  current = null
  opening = null
}

export function resetForTests(): void {
  closeStore()
  subs.clear()
  waiters = []
}

function docRef(ref: unknown): DocumentReference {
  if (!(ref instanceof DocumentReference)) throw new Error('Expected a document reference')
  return ref
}

export async function getDoc(ref: DocumentReference): Promise<DocumentSnapshot> {
  return (await ready()).getDoc(docRef(ref))
}

export async function getDocs(target: CollectionReference | Query): Promise<QuerySnapshot> {
  return (await ready()).getDocs(target)
}

export async function setDoc(ref: DocumentReference, data: Data, options?: { merge?: boolean; coalesce?: boolean }): Promise<WriteReceipt> {
  const item: WriteItem = { kind: 'set', path: docRef(ref).path, data, merge: options?.merge === true }
  return (await ready()).write([item], { coalesce: options?.coalesce === true })
}

export async function updateDoc(ref: DocumentReference, data: Data): Promise<WriteReceipt> {
  return (await ready()).write([{ kind: 'update', path: docRef(ref).path, data }])
}

export async function deleteDoc(ref: DocumentReference): Promise<WriteReceipt> {
  return (await ready()).write([{ kind: 'delete', path: docRef(ref).path }])
}

export async function addDoc(col: CollectionReference, data: Data): Promise<DocumentReference> {
  if (!(col instanceof CollectionReference)) throw new Error('Expected a collection reference')
  const ref = new DocumentReference(`${col.path}/${newIdLocal()}`)
  await (await ready()).write([{ kind: 'set', path: ref.path, data, merge: false }])
  return ref
}

export interface WriteBatch {
  set(ref: DocumentReference, data: Data, options?: { merge?: boolean }): WriteBatch
  update(ref: DocumentReference, data: Data): WriteBatch
  delete(ref: DocumentReference): WriteBatch
  /** `coalesce`: see Store.write — earlier unsent coalescable writes to the same docs fold into this one. */
  commit(opts?: { coalesce?: boolean }): Promise<WriteReceipt>
}

export function writeBatch(_db?: unknown): WriteBatch {
  const items: WriteItem[] = []
  let committed = false
  const batch: WriteBatch = {
    set(ref, data, options) {
      items.push({ kind: 'set', path: docRef(ref).path, data, merge: options?.merge === true })
      return batch
    },
    update(ref, data) {
      items.push({ kind: 'update', path: docRef(ref).path, data })
      return batch
    },
    delete(ref) {
      items.push({ kind: 'delete', path: docRef(ref).path })
      return batch
    },
    async commit(opts) {
      if (committed) throw new Error('This batch has already been committed')
      committed = true
      if (!items.length) return { groupId: '', opIds: [] }
      return (await ready()).write(items, { coalesce: opts?.coalesce === true })
    },
  }
  return batch
}

export function onSnapshot(
  ref: DocumentReference,
  next: (snap: DocumentSnapshot) => void,
  error?: (err: unknown) => void,
): () => void
export function onSnapshot(
  target: CollectionReference | Query,
  next: (snap: QuerySnapshot) => void,
  error?: (err: unknown) => void,
): () => void
export function onSnapshot(
  target: SnapshotTarget,
  next: ((snap: DocumentSnapshot) => void) | ((snap: QuerySnapshot) => void),
  error?: (err: unknown) => void,
): () => void {
  const sub: Sub = { target, next: next as Sub['next'], error, detach: null }
  subs.add(sub)
  if (current) sub.detach = current.onSnapshot(target, sub.next, error)
  return () => {
    subs.delete(sub)
    sub.detach?.()
    sub.detach = null
  }
}
